import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { Link } from "../../lib/router.js";
import { PageSpinner, EmptyState } from "../../components/ui.js";
import { FoodLogModal } from "../../components/FoodLogModal.js";
import { IconClipboard } from "../../components/icons.js";
import { MealsTab } from "./MealsTab.js";
import { PreferencesTab } from "./PreferencesTab.js";
import { RestaurantsTab } from "./RestaurantsTab.js";

interface Targets {
  strategy: string; calories: number; proteinG: number; carbsG: number; fatG: number; fiberG: number; waterOz: number; rationale: string | null;
}
interface LogEntry { id: string; slot: string; description: string; calories: number; proteinG: number }
interface Today {
  targets: Targets | null;
  totals: { calories: number; proteinG: number; carbsG: number; fatG: number; fiberG: number };
  remaining: { calories: number; proteinG: number } | null;
  waterToday: number;
  log: LogEntry[];
  hasProfile: boolean;
}

function todayLocalDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const SLOT_LABELS: Record<string, string> = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snacks" };

export function NutritionPage() {
  const [tab, setTab] = useState<"today" | "meals" | "preferences" | "restaurants">("today");
  return (
    <div>
      <div className="page-header">
        <h1>Nutrition</h1>
        <p className="subtitle">What to eat, how much, and how it fits your day.</p>
      </div>
      <div className="segmented" style={{ marginBottom: "var(--space-5)" }}>
        {([["today", "Today"], ["meals", "Meals"], ["preferences", "Preferences"], ["restaurants", "Restaurants"]] as const).map(([k, l]) => (
          <button key={k} className={tab === k ? "active" : ""} onClick={() => setTab(k)} type="button">{l}</button>
        ))}
      </div>
      {tab === "today" && <TodayTab />}
      {tab === "meals" && <MealsTab />}
      {tab === "preferences" && <PreferencesTab />}
      {tab === "restaurants" && <RestaurantsTab />}
    </div>
  );
}

function Bar({ label, value, target, unit, color = "var(--accent)" }: { label: string; value: number; target: number; unit: string; color?: string }) {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const over = value > target && target > 0;
  return (
    <div style={{ marginBottom: "var(--space-3)" }}>
      <div className="row-between text-sm" style={{ marginBottom: 4 }}>
        <span className="muted">{label}</span>
        <span><strong>{Math.round(value)}</strong> <span className="dim">/ {target}{unit}</span></span>
      </div>
      <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%`, background: over ? "var(--warning)" : color }} /></div>
    </div>
  );
}

function TodayTab() {
  const [data, setData] = useState<Today | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const date = todayLocalDate();

  const load = useCallback(async () => {
    try {
      setData(await api.get<Today>(`/api/nutrition/today?date=${date}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load nutrition.");
    }
  }, [date]);
  useEffect(() => { void load(); }, [load]);

  async function recompute() {
    setRecomputing(true);
    try {
      await api.post("/api/nutrition/targets/recompute");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not calculate.");
    } finally {
      setRecomputing(false);
    }
  }

  async function del(id: string) {
    await api.del(`/api/nutrition/log/${id}`).catch(() => undefined);
    await load();
  }

  if (!data) return error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />;

  if (!data.targets) {
    return (
      <EmptyState icon={<IconClipboard />} title="Your numbers need a few details" action={
        data.hasProfile
          ? <button className="btn btn-sm mt-4" onClick={recompute} disabled={recomputing}>{recomputing ? "Calculating…" : "Calculate my numbers"}</button>
          : <Link to="/life-profile" className="btn btn-sm mt-4">Open Life Profile</Link>
      }>
        ClovaForm needs your age, sex, height, and current weight to calculate your daily calories and protein. Add them in your Life Profile{data.hasProfile ? ", then calculate" : ""}.
      </EmptyState>
    );
  }

  const t = data.targets;
  const byslot = ["breakfast", "lunch", "dinner", "snack"].map((s) => ({ slot: s, entries: data.log.filter((e) => e.slot === s) })).filter((g) => g.entries.length > 0);

  return (
    <div className="stack">
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Your numbers */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Your numbers</span>
          <button className="btn btn-sm btn-ghost" onClick={recompute} disabled={recomputing}>{recomputing ? "…" : "Recalculate"}</button>
        </div>
        <span className="badge badge-accent">{t.strategy}</span>
        <div className="stat-grid mt-4">
          <div className="stat"><div className="stat-label">Calories</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{t.calories}</div></div>
          <div className="stat"><div className="stat-label">Protein</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{t.proteinG}g</div></div>
          <div className="stat"><div className="stat-label">Carbs</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{t.carbsG}g</div></div>
          <div className="stat"><div className="stat-label">Fat</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{t.fatG}g</div></div>
          <div className="stat"><div className="stat-label">Fiber</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{t.fiberG}g</div></div>
          <div className="stat"><div className="stat-label">Water</div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{t.waterOz}oz</div></div>
        </div>
        {t.rationale && <p className="dim text-xs mt-4" style={{ marginBottom: 0 }}>{t.rationale}</p>}
      </div>

      {/* Today's progress */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Today</span>
          <button className="btn btn-sm btn-primary" onClick={() => setLogging(true)}>+ Log food</button>
        </div>
        <Bar label="Calories" value={data.totals.calories} target={t.calories} unit="" />
        <Bar label="Protein" value={data.totals.proteinG} target={t.proteinG} unit="g" color="var(--accent-leaf)" />
        <Bar label="Carbs" value={data.totals.carbsG} target={t.carbsG} unit="g" color="var(--accent-cyan)" />
        <Bar label="Fat" value={data.totals.fatG} target={t.fatG} unit="g" color="var(--accent-violet)" />
        <Bar label="Water" value={data.waterToday} target={t.waterOz} unit="oz" color="var(--accent)" />
        {data.remaining && data.remaining.calories >= 0 && (
          <p className="dim text-sm mt-4" style={{ marginBottom: 0 }}>{data.remaining.calories} kcal and {Math.max(0, data.remaining.proteinG)}g protein left today.</p>
        )}
      </div>

      {/* Log */}
      {byslot.length > 0 ? (
        byslot.map((g) => (
          <div className="card" key={g.slot} style={{ padding: 0, overflow: "hidden" }}>
            <div className="nav-section-label" style={{ padding: "var(--space-4) var(--space-4) var(--space-2)" }}>{SLOT_LABELS[g.slot]}</div>
            {g.entries.map((e) => (
              <div className="task-row" key={e.id} style={{ alignItems: "center" }}>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>{e.description}</div>
                  <div className="dim text-xs">{Math.round(e.calories)} kcal · {Math.round(e.proteinG)}g protein</div>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={() => del(e.id)}>Remove</button>
              </div>
            ))}
          </div>
        ))
      ) : (
        <p className="dim text-sm center">Nothing logged yet today. Tap “+ Log food” to start.</p>
      )}

      {logging && <FoodLogModal date={date} onClose={() => setLogging(false)} onLogged={load} />}
    </div>
  );
}
